import { doc, onSnapshot, setDoc, getDoc, updateDoc, collection, query, where, getDocs, limit } from "firebase/firestore";
import type { OnboardingAnswerValue, OnboardingUser, User, Character, UTMParameters, KinkProfile } from "../types";
import { db } from "../firebase";
import { userConverter, onboardingUserConverter, characterConverter } from "./converters";
import { getChatsByUserId } from "./chatService";
import { getOrCreateVisitorId } from "./visitorIdentityService";
import { handleSnapshotError } from "../utils/recoveryErrors";
import { reportError } from "../utils/reportError";
import type { PostQuizChatMode } from "../abTesting/abTests";
import {
  createGlobalABTestManager,
  FORCED_POST_QUIZ_CHAT_MODE,
} from "../abTesting/abTests";
import { isVariantOverrideAllowed } from "../utils/ABTestingCore";
import {
  extractAppearanceAnswers,
  hasActiveScript,
  hasSpicyPhoto,
  pickTiedCharacterId,
  selectMatchedCharacterId,
} from "./onboardingMatching";

// Re-exported for the existing tie-break tests; the implementation lives in
// ./onboardingMatching so the coverage audit script can share it.
export { pickTiedCharacterId };

const USERS_COLLECTION = "users";
const ONBOARDING_USERS_COLLECTION = "usersOnboardings";
const ONBOARDING_ATTEMPTS_COLLECTION = "usersOnboardingAttempts";
const CHARACTERS_COLLECTION = "characters";

export type FbclidAttributionSource =
  | "client_capture"
  | "checkout"
  | "stripe_webhook"
  | "curo_webhook";

export interface UserFbclidAttribution {
  fbclid?: string;
  fbclidCapturedAt?: Date;
  fbclidLastSeenAt?: Date;
  fbclidSource?: FbclidAttributionSource;
}

export interface UserUtmAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  exocode?: string;
  referrer?: string;
  landingPage?: string;
  utmCapturedAt?: Date;
  utmLastSeenAt?: Date;
}

function isFirestoreNotFoundError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: string }).code === "not-found"
  );
}

function normalizeFbclid(value: string): string | undefined {
  const normalized = value.trim();
  return normalized || undefined;
}

function coerceDate(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return value;
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    try {
      return (value as { toDate: () => Date }).toDate();
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export async function createUserDocument(
  userId: string,
  email?: string,
  onboardingFlowId?: string,
  preferredLanguage?: "en" | "de"
): Promise<void> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter
    );
    // Only include email if it is defined
    const payload: User = {
      id: userId,
      visitorId: getOrCreateVisitorId(),
      ...(email ? { email } : {}),
      ...(onboardingFlowId ? { onboardingFlowId } : {}),
      ...(preferredLanguage ? { preferredLanguage } : {}),
    };

    await setDoc(userRef, payload, { merge: true });
  } catch (error) {
    console.error("Error creating user document:", error);
    throw error;
  }
}

export async function syncUserEmail(userId: string, email: string): Promise<void> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    return;
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter
    );
    await setDoc(
      userRef,
      {
        id: userId,
        email: normalizedEmail,
        updatedAt: new Date(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error syncing user email to users collection:", error);
    throw error;
  }
}

export async function createUserOnboardingDocument(
  userId: string,
  onboardingFlowId?: string,
  utmParameters?: import("../types").UTMParameters
): Promise<void> {
  try {
    const userRef = doc(db, ONBOARDING_USERS_COLLECTION, userId).withConverter(
      onboardingUserConverter
    );
    const now = new Date();

    const payload: OnboardingUser = {
      id: userId,
      ...(onboardingFlowId ? { onboardingFlowId } : {}),
      ...(utmParameters ? { utmParameters } : {}),
      createdAt: now,
      updatedAt: now,
    };

    await setDoc(userRef, payload, { merge: true });
  } catch (error) {
    console.error("Error creating onboarding user document:", error);
    reportError(error, {
      area: "onboarding",
      op: "persist_onboarding_user",
      extra: { uid: userId },
    });
    throw error;
  }
}

export async function createOnboardingAttemptDocument(
  userId: string,
  onboardingFlowId?: string,
  customerId?: string
): Promise<string> {
  try {
    const attemptRef = doc(collection(db, ONBOARDING_ATTEMPTS_COLLECTION));
    const now = new Date();
    await setDoc(attemptRef, {
      uid: userId,
      authUid: userId,
      ...(onboardingFlowId ? { onboardingFlowId } : {}),
      ...(customerId ? { customerId } : {}),
      startedAt: now,
      updatedAt: now,
    });
    return attemptRef.id;
  } catch (error) {
    console.error("Error creating onboarding attempt document:", error);
    reportError(error, {
      area: "onboarding",
      op: "persist_onboarding_attempt",
      extra: { uid: userId },
    });
    throw error;
  }
}

export async function saveOnboardingAttemptAnswer(
  attemptId: string,
  questionId: string,
  answer: OnboardingAnswerValue
): Promise<void> {
  try {
    const attemptRef = doc(db, ONBOARDING_ATTEMPTS_COLLECTION, attemptId);
    await setDoc(
      attemptRef,
      {
        answers: {
          [questionId]: answer,
        },
        updatedAt: new Date(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error(`Error saving onboarding attempt answer for question ${questionId}:`, error);
    reportError(error, {
      area: "onboarding",
      op: "persist_attempt_answer",
      extra: { attemptId, questionId },
    });
    throw error;
  }
}

export async function completeOnboardingAttempt(
  attemptId: string,
  matchedCharacterId?: string
): Promise<void> {
  try {
    const attemptRef = doc(db, ONBOARDING_ATTEMPTS_COLLECTION, attemptId);
    const now = new Date();
    await setDoc(
      attemptRef,
      {
        ...(matchedCharacterId ? { matchedCharacterId } : {}),
        completedAt: now,
        updatedAt: now,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error completing onboarding attempt:", error);
    reportError(error, {
      area: "onboarding",
      op: "persist_onboarding_completion",
      extra: { attemptId },
    });
    throw error;
  }
}

export async function getUserDocument(userId: string): Promise<User | null> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter
    );
    const snapshot = await getDoc(userRef);
    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    console.error("Error fetching user document:", error);
    throw error;
  }
}

export async function saveUserFbclidAttribution(
  userId: string,
  fbclid: string,
  source: FbclidAttributionSource,
): Promise<void> {
  const normalizedFbclid = normalizeFbclid(fbclid);
  if (!normalizedFbclid) {
    return;
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter,
    );
    const existingSnapshot = await getDoc(userRef);
    const now = new Date();
    const existingCapturedAt = existingSnapshot.exists() ?
      coerceDate(existingSnapshot.data()?.attribution?.fbclidCapturedAt) :
      undefined;

    await setDoc(
      userRef,
      {
        id: userId,
        attribution: {
          fbclid: normalizedFbclid,
          fbclidCapturedAt: existingCapturedAt || now,
          fbclidLastSeenAt: now,
          fbclidSource: source,
        },
        updatedAt: now,
      },
      {merge: true},
    );
  } catch (error) {
    console.error("Error saving user fbclid attribution:", error);
    throw error;
  }
}

export async function getUserFbclidAttribution(
  userId: string,
): Promise<UserFbclidAttribution | null> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter,
    );
    const snapshot = await getDoc(userRef);
    if (!snapshot.exists()) {
      return null;
    }

    const rawAttribution = snapshot.data()?.attribution;
    if (!rawAttribution || typeof rawAttribution !== "object") {
      return null;
    }

    const fbclid = normalizeFbclid(
      typeof rawAttribution.fbclid === "string" ? rawAttribution.fbclid : "",
    );
    if (!fbclid) {
      return null;
    }

    return {
      fbclid,
      fbclidCapturedAt: coerceDate(rawAttribution.fbclidCapturedAt),
      fbclidLastSeenAt: coerceDate(rawAttribution.fbclidLastSeenAt),
      fbclidSource:
        typeof rawAttribution.fbclidSource === "string" ?
          rawAttribution.fbclidSource as FbclidAttributionSource :
          undefined,
    };
  } catch (error) {
    console.error("Error reading user fbclid attribution:", error);
    throw error;
  }
}

export async function saveUserUtmAttribution(
  userId: string,
  utmParams: UTMParameters,
): Promise<void> {
  const hasSignal = Boolean(
    utmParams.utm_source ||
    utmParams.utm_medium ||
    utmParams.utm_campaign ||
    utmParams.exocode ||
    utmParams.referrer,
  );
  if (!hasSignal) {
    return;
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter,
    );
    const existingSnapshot = await getDoc(userRef);
    const now = new Date();
    const existingCapturedAt = existingSnapshot.exists() ?
      coerceDate(existingSnapshot.data()?.attribution?.utmCapturedAt) :
      undefined;

    await setDoc(
      userRef,
      {
        id: userId,
        attribution: {
          ...(utmParams.utm_source ? { utmSource: utmParams.utm_source } : {}),
          ...(utmParams.utm_medium ? { utmMedium: utmParams.utm_medium } : {}),
          ...(utmParams.utm_campaign ? { utmCampaign: utmParams.utm_campaign } : {}),
          ...(utmParams.utm_content ? { utmContent: utmParams.utm_content } : {}),
          ...(utmParams.utm_term ? { utmTerm: utmParams.utm_term } : {}),
          ...(utmParams.exocode ? { exocode: utmParams.exocode } : {}),
          ...(utmParams.referrer ? { referrer: utmParams.referrer } : {}),
          ...(utmParams.landing_page ? { landingPage: utmParams.landing_page } : {}),
          utmCapturedAt: existingCapturedAt || now,
          utmLastSeenAt: now,
        },
        updatedAt: now,
      },
      {merge: true},
    );
  } catch (error) {
    console.error("Error saving user UTM attribution:", error);
    throw error;
  }
}

export async function updateUserPreferredLanguage(
  userId: string,
  preferredLanguage: "en" | "de"
): Promise<void> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter
    );
    await updateDoc(userRef, {
      preferredLanguage,
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("Error updating user preferred language:", error);
    throw error;
  }
}

export async function updateUserEmailPreferences(
  userId: string,
  nonTransactionalOptOut: boolean
): Promise<void> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter
    );
    const now = new Date();
    await updateDoc(userRef, {
      "emailPreferences.nonTransactionalOptOut": nonTransactionalOptOut,
      "emailPreferences.updatedAt": now,
      updatedAt: now,
    });
  } catch (error) {
    console.error("Error updating user email preferences:", error);
    throw error;
  }
}

export async function updateUserLastActive(userId: string): Promise<void> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter
    );
    const now = new Date();
    await updateDoc(userRef, {
      lastActiveAt: now,
      asyncMessageCount: 0,
      updatedAt: now,
    });
  } catch (error) {
    if (isFirestoreNotFoundError(error)) {
      return;
    }
    console.error("Error updating user lastActiveAt:", error);
  }
}

export async function updateUserActiveChat(
  userId: string,
  activeChatId: string | null
): Promise<void> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter
    );
    await updateDoc(userRef, {
      activeChatId,
      updatedAt: new Date(),
    });
  } catch (error) {
    if (isFirestoreNotFoundError(error)) {
      return;
    }
    console.error("Error updating user activeChatId:", error);
  }
}

export async function updateUserDisplayName(
  userId: string,
  displayName: string
): Promise<void> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
      userConverter
    );
    await updateDoc(userRef, {
      displayName: displayName.trim(),
      updatedAt: new Date(),
    });
  } catch (error) {
    if (isFirestoreNotFoundError(error)) {
      return;
    }
    console.error("Error updating user displayName:", error);
  }
}

export async function getOnboardingUserDocument(userId: string): Promise<OnboardingUser | null> {
  try {
    const userRef = doc(db, ONBOARDING_USERS_COLLECTION, userId).withConverter(
      onboardingUserConverter
    );
    const snapshot = await getDoc(userRef);
    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    console.error("Error fetching user document:", error);
    throw error;
  }
}

export async function saveOnboardingUserAnswer(
  userId: string,
  questionId: string,
  answer: string | string[] | number | boolean,
  onboardingFlowId?: string
): Promise<void> {
  try {
    const userRef = doc(db, ONBOARDING_USERS_COLLECTION, userId).withConverter(
      onboardingUserConverter
    );
    const now = new Date();

    // Check if document exists, if not initialize it with required fields
    const docSnapshot = await getDoc(userRef);
    const payload: Partial<OnboardingUser> = {
      answers: {
        [questionId]: answer,
      },
      updatedAt: now,
    };

    // If document doesn't exist, add creation fields
    if (!docSnapshot.exists()) {
      payload.id = userId;
      payload.createdAt = now;
      payload.authUid = userId;
      if (onboardingFlowId) {
        payload.onboardingFlowId = onboardingFlowId;
      }
    }

    await setDoc(userRef, payload, { merge: true });
  } catch (error) {
    console.error(`Error saving onboarding user answer for question ${questionId}:`, error);
    reportError(error, {
      area: "onboarding",
      op: "persist_onboarding_answer",
      extra: { uid: userId, questionId },
    });
    throw error;
  }
}

export async function saveOnboardingUserEmail(
  userId: string,
  email: string,
  onboardingFlowId?: string
): Promise<void> {
  try {
    const userRef = doc(db, ONBOARDING_USERS_COLLECTION, userId).withConverter(
      onboardingUserConverter
    );
    const now = new Date();

    // Check if document exists, if not initialize it with required fields
    const docSnapshot = await getDoc(userRef);
    const normalizedEmail = email.trim().toLowerCase();
    const payload: Partial<OnboardingUser> = {
      email: normalizedEmail,
      updatedAt: now,
    };

    // If document doesn't exist, add creation fields
    if (!docSnapshot.exists()) {
      payload.id = userId;
      payload.createdAt = now;
      payload.authUid = userId;
      if (onboardingFlowId) {
        payload.onboardingFlowId = onboardingFlowId;
      }
    }

    await setDoc(userRef, payload, { merge: true });
    await syncUserEmail(userId, normalizedEmail);
  } catch (error) {
    console.error(`Error saving onboarding user email:`, error);
    reportError(error, {
      area: "onboarding",
      op: "persist_onboarding_email",
      extra: { uid: userId },
    });
    throw error;
  }
}

export async function saveOnboardingUserAnswers(
  userId: string,
  answers: Record<string, string | string[] | number | boolean>
): Promise<void> {
  try {
    const userRef = doc(db, ONBOARDING_USERS_COLLECTION, userId).withConverter(
      onboardingUserConverter
    );
    const now = new Date();

    await setDoc(
      userRef,
      {
        answers,
        updatedAt: now,
      },
      { merge: true }
    );
  } catch (error) {
    console.error(`Error saving user answers:`, error);
    reportError(error, {
      area: "onboarding",
      op: "persist_onboarding_answers",
      extra: { uid: userId },
    });
    throw error;
  }
}

export async function setOnboardingUserWillingToPay(
  userId: string
): Promise<void> {
  try {
    const userRef = doc(db, ONBOARDING_USERS_COLLECTION, userId).withConverter(
      onboardingUserConverter
    );
    const now = new Date();

    await setDoc(
      userRef,
      {
        willingToPay: true,
        updatedAt: now,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error setting onboarding user willingToPay flag:", error);
    reportError(error, {
      area: "onboarding",
      op: "persist_willing_to_pay",
      extra: { uid: userId },
    });
    throw error;
  }
}

export async function setOnboardingMatchedCharacter(
  userId: string,
  characterId: string
): Promise<void> {
  try {
    const userRef = doc(db, ONBOARDING_USERS_COLLECTION, userId).withConverter(
      onboardingUserConverter
    );
    const now = new Date();

    await setDoc(
      userRef,
      {
        matchedCharacterId: characterId,
        updatedAt: now,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error setting onboarding matched character:", error);
    reportError(error, {
      area: "onboarding",
      op: "persist_matched_character",
      extra: { uid: userId, characterId },
    });
    throw error;
  }
}

export async function saveOnboardingKinkProfile(
  userId: string,
  attemptId: string | null,
  profile: KinkProfile
): Promise<void> {
  try {
    const userRef = doc(db, ONBOARDING_USERS_COLLECTION, userId).withConverter(
      onboardingUserConverter
    );
    const now = new Date();

    await setDoc(userRef, { kinkProfile: profile, updatedAt: now }, { merge: true });

    if (attemptId) {
      const attemptRef = doc(db, ONBOARDING_ATTEMPTS_COLLECTION, attemptId);
      await setDoc(attemptRef, { kinkProfile: profile, updatedAt: now }, { merge: true });
    }
  } catch (error) {
    console.error("Error saving onboarding kink profile:", error);
    reportError(error, {
      area: "onboarding",
      op: "persist_kink_profile",
      extra: { uid: userId, attemptId },
    });
    throw error;
  }
}

export function observeOnboardingUserDocument(
  userId: string,
  callback: (user: OnboardingUser | null) => void,
  onError?: (error: Error) => void
): () => void {
  const userRef = doc(db, ONBOARDING_USERS_COLLECTION, userId).withConverter(
    onboardingUserConverter
  );

  return onSnapshot(
    userRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      callback(snapshot.data());
    },
    (error) => handleSnapshotError("onboarding_user_doc", error, onError)
  );
}

export function observeUserDocument(
  userId: string,
  callback: (user: User | null) => void,
  onError?: (error: Error) => void
): () => void {
  const userRef = doc(db, USERS_COLLECTION, userId).withConverter(
    userConverter
  );

  return onSnapshot(
    userRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      callback(snapshot.data());
    },
    (error) => handleSnapshotError("user_doc", error, onError)
  );
}

export function observeUnlockedMedia(
  userId: string,
  callback: (unlockedMediaIds: string[]) => void,
  onError?: (error: Error) => void
): () => void {
  const unlockedRef = collection(db, USERS_COLLECTION, userId, "unlockedMedia");

  return onSnapshot(
    unlockedRef,
    (snapshot) => {
      const unlockedIds = snapshot.docs.map((docItem) => docItem.id);
      callback(unlockedIds);
    },
    (error) => handleSnapshotError("unlocked_media", error, onError)
  );
}

export function observeUserCharacters(
  userId: string,
  callback: (characters: Character[]) => void,
  onError?: (error: Error) => void
): () => void {
  const charactersRef = collection(db, CHARACTERS_COLLECTION).withConverter(characterConverter);
  const q = query(charactersRef, where("userId", "==", userId));

  return onSnapshot(
    q,
    (snapshot) => {
      const characters = snapshot.docs
        .map((doc) => doc.data())
        .sort((a, b) => {
          const aTime = a.createdAt?.getTime() ?? 0;
          const bTime = b.createdAt?.getTime() ?? 0;
          return bTime - aTime; // Newest first
        });
      callback(characters);
    },
    (error) => handleSnapshotError("user_characters", error, onError)
  );
}

export async function getCharacterById(characterId: string): Promise<Character | null> {
  try {
    const characterRef = doc(db, CHARACTERS_COLLECTION, characterId).withConverter(
      characterConverter
    );
    const snapshot = await getDoc(characterRef);
    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    console.error("Error fetching character:", error);
    throw error;
  }
}

export async function getPublicCharacters(): Promise<Character[]> {
  try {
    const charactersRef = collection(db, CHARACTERS_COLLECTION).withConverter(characterConverter);
    const q = query(charactersRef, where("isPublic", "==", true));
    const snapshot = await getDocs(q);
    
    const characters = snapshot.docs
      .map((doc) => doc.data())
      .sort((a, b) => {
        const aTime = a.createdAt?.getTime() ?? 0;
        const bTime = b.createdAt?.getTime() ?? 0;
        return bTime - aTime; // Newest first
      });
    
    return characters;
  } catch (error) {
    console.error("Error fetching public characters:", error);
    throw error;
  }
}

export async function getPrivateCharacters(): Promise<Character[]> {
  try {
    const charactersRef = collection(db, CHARACTERS_COLLECTION).withConverter(characterConverter);
    const q = query(charactersRef, where("isPublic", "==", false));
    const snapshot = await getDocs(q);

    const characters = snapshot.docs
      .map((doc) => doc.data())
      .sort((a, b) => {
        const aTime = a.createdAt?.getTime() ?? 0;
        const bTime = b.createdAt?.getTime() ?? 0;
        return bTime - aTime; // Newest first
      });

    return characters;
  } catch (error) {
    console.error("Error fetching private characters:", error);
    throw error;
  }
}

/**
 * Characters carrying `activeScriptId` (scripted-chat MVP pilots — see
 * apps/competition/mechat/scripted-chat-spec.md), regardless of `isPublic`.
 * `activeScriptId` is only ever a non-empty string when present, so `> ""`
 * matches "field exists and is non-empty" — a single-field range filter,
 * which Firestore auto-indexes, so this needs no composite index and no
 * full-collection scan.
 */
export async function getScriptPilotCharacters(): Promise<Character[]> {
  try {
    const charactersRef = collection(db, CHARACTERS_COLLECTION).withConverter(characterConverter);
    const q = query(charactersRef, where("activeScriptId", ">", ""));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data());
  } catch (error) {
    console.error("Error fetching script-pilot characters:", error);
    throw error;
  }
}

/**
 * The `ai` arm's matching pool for `postQuizChatMode` (see ../abTesting/abTests.ts):
 * the ordinary private-character pool minus every scripted-chat pilot and every
 * premium-only character.
 *
 * Both exclusions are load-bearing:
 * - `activeScriptId` — five of the six production pilots are also
 *   `isPublic == false`, so without this filter most of the "ai" arm would be
 *   matched onto a pilot and get a scripted chat anyway, making the arm a null
 *   test.
 * - `isPremiumOnly` — a premium-gated character (Elise on production) would put
 *   a subscription wall between the quiz and the first message, which is not
 *   the experience being compared.
 *
 * Filtered client-side rather than queried: `where("activeScriptId", "==", null)`
 * does not match documents that simply lack the field, and combining an
 * inequality on `activeScriptId` with the `isPublic` equality would need a
 * composite index. The pool is ~77 documents, so one query and a filter is the
 * cheaper as well as the simpler option. (Note it is still the heavier of the
 * two arms — roughly 2.2 MB of character docs against the pilot query's
 * ~460 KB.)
 */
export async function getUnscriptedPrivateCharacters(): Promise<Character[]> {
  const privateCharacters = await getPrivateCharacters();
  return privateCharacters.filter((character) => {
    const hasScript =
      typeof character.activeScriptId === "string" && character.activeScriptId.length > 0;
    return !hasScript && character.isPremiumOnly !== true;
  });
}

/**
 * Resolves the `postQuizChatMode` arm for this visitor.
 *
 * Deliberately re-reads the assignment on every call instead of caching it:
 * the value is stored in localStorage by `ABTestManager`, so repeated calls
 * (the first match on `summary`, the router's `completeOnboarding`, and the
 * re-match on the success screen) all return the same variant, while a
 * `?ab_postQuizChatMode=` override applied mid-session is still picked up.
 *
 * Precedence mirrors OnboardingPage's handling of `kinkQuizVersion`: on
 * dev/staging a `?ab_postQuizChatMode=` URL override wins over the FORCED
 * constant (so both arms stay testable there once the test is ended by setting
 * it); otherwise FORCED_POST_QUIZ_CHAT_MODE wins over the assignment.
 *
 * Never throws. Any failure (no `window`, localStorage blocked, an invalid
 * config) degrades to "scripted", i.e. the pre-test behaviour.
 */
export function resolvePostQuizChatMode(): PostQuizChatMode {
  try {
    const hasUrlOverride =
      isVariantOverrideAllowed() &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("ab_postQuizChatMode");

    if (!hasUrlOverride && FORCED_POST_QUIZ_CHAT_MODE) {
      return FORCED_POST_QUIZ_CHAT_MODE;
    }

    const variant = createGlobalABTestManager().getVariant("postQuizChatMode");
    return variant === "ai" ? "ai" : "scripted";
  } catch (error) {
    console.warn("Failed to resolve postQuizChatMode, falling back to scripted:", error);
    return "scripted";
  }
}

/**
 * The `scripted` arm's matching pool for `postQuizChatMode` (see
 * ../abTesting/abTests.ts and docs/scripted-chat-visual-matching-plan.md):
 * every scripted-chat pilot (`activeScriptId`, regardless of `isPublic` /
 * `isPremiumOnly` — exactly the pre-2026-09 pool) **plus** every private,
 * non-premium character that has at least one spicy photo.
 *
 * The widening is the whole fix for "the user asked for a redhead and got
 * whichever of six pilots scored highest": the script is no longer a property
 * of the character — `create-chat` assigns a shared script to any character
 * matched in the `scripted` arm — so matching is free to pick by looks over
 * the full pool. The spicy-photo requirement exists because the scripted
 * scene's media beats (the conversion) need something to send; pilots are
 * exempt from it because their `scriptMediaMap` is wired by hand.
 *
 * Both queries are the ones that already exist (`activeScriptId > ""` and
 * `isPublic == false`), merged and de-duplicated by id — a pilot that is also
 * private appears once, as a pilot.
 */
export async function getScriptedMatchingPool(): Promise<Character[]> {
  const [scriptPilotCharacters, privateCharacters] = await Promise.all([
    getScriptPilotCharacters(),
    getPrivateCharacters(),
  ]);

  const pool = new Map<string, Character>();
  scriptPilotCharacters.forEach((character) => pool.set(character.id, character));
  privateCharacters.forEach((character) => {
    if (pool.has(character.id)) return;
    if (hasActiveScript(character)) {
      // A pilot the pilot query missed (should not happen — `activeScriptId`
      // is always non-empty when present). Treat it as a pilot anyway rather
      // than subjecting it to the media requirement.
      pool.set(character.id, character);
      return;
    }
    if (character.isPremiumOnly === true) return;
    if (!hasSpicyPhoto(character)) return;
    pool.set(character.id, character);
  });

  return Array.from(pool.values());
}

export async function matchPrivateCharacterFromOnboarding(
  userOnboarding: OnboardingUser,
  options?: { excludedCharacterIds?: string[] }
): Promise<string | null> {
  if (!userOnboarding.answers) {
    console.warn("No onboarding answers found for user:", userOnboarding.id);
    return null;
  }

  const answers = extractAppearanceAnswers(userOnboarding.answers);

  // `postQuizChatMode` (see ../abTesting/abTests.ts) is resolved HERE, not at
  // the call sites, and that is the whole point: this function is called from
  // three places — OnboardingRouter's completeOnboarding, SummaryScreen, and
  // OnboardingSuccessScreen's re-match with `excludedCharacterIds` — and a
  // user must never get one arm on the first match and the other on a
  // re-match. One resolution point makes that structural rather than a
  // convention three files have to remember.
  const postQuizChatMode = resolvePostQuizChatMode();
  let candidatePool: Character[] = [];

  if (postQuizChatMode === "ai") {
    // "ai" arm: the ordinary private pool minus pilots and premium-only
    // characters, so the matched character carries no script and the server
    // opens a normal AI chat. A query failure is swallowed on purpose — the
    // fallback below restores the "scripted" behaviour rather than
    // dead-ending the funnel (OnboardingSuccessScreen throws
    // "No available private character found." on a null re-match).
    try {
      candidatePool = await getUnscriptedPrivateCharacters();
    } catch (error) {
      console.error("Failed to build the unscripted matching pool:", error);
      candidatePool = [];
    }
  }

  if (candidatePool.length === 0) {
    // "scripted" arm — and the safety net for an "ai" arm whose pool came back
    // empty or failed.
    //
    // Pilots plus every private character with spicy media (see
    // getScriptedMatchingPool). Whether the chat then plays a script is
    // decided server-side by `create-chat` from the arm it is handed, not
    // from the character: a pilot plays its own script, anyone else is
    // assigned a shared one. When no character exists at all there is
    // nothing to match — same null as before.
    candidatePool = await getScriptedMatchingPool();
    if (candidatePool.length === 0) {
      return null;
    }
  }

  const excludedCharacterIds = new Set(options?.excludedCharacterIds ?? []);
  if (!options?.excludedCharacterIds) {
    const chats = await getChatsByUserId(userOnboarding.id);
    chats.forEach((chat) => excludedCharacterIds.add(chat.characterId));
  }

  const candidates = candidatePool.filter(
    (character) => !excludedCharacterIds.has(character.id)
  );

  if (candidates.length === 0) {
    return null;
  }

  // Score, keep the best, prefer a pilot among equally good looks (its
  // scriptMediaMap is hand-wired to the scene), then break the remaining tie
  // deterministically (see `pickTiedCharacterId`). The seed is the onboarding
  // document id — stable for the whole lifetime of the user, unlike `uid`,
  // which is only attached once auth is linked.
  return selectMatchedCharacterId(answers, candidates, userOnboarding.id, {
    preferScripted: postQuizChatMode !== "ai",
  });
}

const MAX_AGE = 36;

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => `"${key}":${stableStringify(val)}`);
    return `{${entries.join(",")}}`;
  }

  return JSON.stringify(value);
}

function normalizeAnswers(
  answers: Record<string, string | string[] | number | boolean>
): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};
  Object.keys(answers)
    .sort((a, b) => a.localeCompare(b))
    .forEach((key) => {
      const value = answers[key];
      if (Array.isArray(value)) {
        normalized[key] = [...value].map(String).sort();
      } else {
        normalized[key] = value;
      }
    });
  return normalized;
}

function hashString(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Parses an age value from onboarding answers.
 * Handles formats: "26" (single), "21-28" (range), "26+" (open range)
 * For ranges, returns a random number within the range.
 * Max age is capped at 36.
 */
export function parseAge(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null) return undefined;

  // If already a number, clamp to max
  if (typeof value === "number") {
    return Math.min(value, MAX_AGE);
  }

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Open range: "26+"
  if (trimmed.endsWith("+")) {
    const min = parseInt(trimmed.slice(0, -1), 10);
    if (isNaN(min)) return undefined;
    return Math.floor(Math.random() * (MAX_AGE - min + 1)) + min;
  }

  // Range: "21-28"
  if (trimmed.includes("-")) {
    const [minStr, maxStr] = trimmed.split("-");
    const min = parseInt(minStr, 10);
    const max = Math.min(parseInt(maxStr, 10), MAX_AGE);
    if (isNaN(min) || isNaN(max)) return undefined;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  // Single number: "26"
  const single = parseInt(trimmed, 10);
  if (isNaN(single)) return undefined;
  return Math.min(single, MAX_AGE);
}

/**
 * @deprecated Use matchPublicCharacterFromOnboarding instead.
 */
export async function createCharacterFromOnboarding(
  userOnboarding: OnboardingUser
): Promise<string | null> {
  try {
    if (!userOnboarding.answers) {
      console.warn("No onboarding answers found for user:", userOnboarding.id);
      return null;
    }

    const answers = userOnboarding.answers;
    const normalizedAnswers = normalizeAnswers(answers);
    const onboardingHash = hashString(stableStringify(normalizedAnswers));

    // Check if a character with the same onboarding answers already exists
    const charactersRef = collection(db, CHARACTERS_COLLECTION).withConverter(
      characterConverter
    );
    const existingQuery = query(
      charactersRef,
      where("userId", "==", userOnboarding.id),
      where("onboardingHash", "==", onboardingHash),
      limit(1)
    );
    const existingSnapshot = await getDocs(existingQuery);

    if (!existingSnapshot.empty) {
      const existingCharacter = existingSnapshot.docs[0].data();
      console.log(
        `Character already exists for user ${userOnboarding.id}: ${existingCharacter.id}`
      );
      return existingCharacter.id;
    }

    const now = new Date();
    const characterId = `${userOnboarding.id}_${now.getTime()}`;

    const characterRef = doc(db, CHARACTERS_COLLECTION, characterId).withConverter(
      characterConverter
    );

    const character: Character = {
      id: characterId,
      userId: userOnboarding.id,
      // Identity
      name: typeof answers.name === "string" ? answers.name : undefined,
      age: parseAge(answers.age as string | number | undefined),
      // Physical attributes
      ethnicity: typeof answers.ethnicity === "string" ? answers.ethnicity : undefined,
      hairColor: typeof answers.hairColor === "string" ? answers.hairColor : undefined,
      breastSize: typeof answers.breastSize === "string" ? answers.breastSize : undefined,
      figure: typeof answers.figure === "string" ? answers.figure : undefined,
      // Personality & preferences
      // v1's "goal" question is multi-select (array); v2's is single-select (string) — normalize both.
      goal: Array.isArray(answers.goal)
        ? answers.goal as string[]
        : typeof answers.goal === "string"
          ? [answers.goal]
          : undefined,
      specificPreferences: Array.isArray(answers.specificPreferences)
        ? answers.specificPreferences as string[]
        : undefined,
      favoriteScenarios: Array.isArray(answers.favoriteScenarios)
        ? answers.favoriteScenarios as string[]
        : undefined,
      // Intensity sliders
      libidoIntensity: typeof answers.libidoIntensity === "number" ? answers.libidoIntensity : undefined,
      kinkOpenness: typeof answers.kinkOpenness === "number" ? answers.kinkOpenness : undefined,
      comfortWithNudity: typeof answers.comfortWithNudity === "number" ? answers.comfortWithNudity : undefined,
      onboardingHash,
      isPublic: false,
      // Metadata
      createdAt: now,
      updatedAt: now,
    };

    await setDoc(characterRef, character);

    console.log(`Created new character for user ${userOnboarding.id}: ${characterId}`);

    return characterId;
  } catch (error) {
    console.error("Error creating character from onboarding:", error);
    reportError(error, {
      area: "onboarding",
      op: "create_character",
      extra: { uid: userOnboarding.id },
    });
    throw error;
  }
}

function normalizePrompt(value?: string | null): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function buildIdentityPromptPrefix(character: Character): string {
  const segments: string[] = [];

  const name = character.name || "unnamed";
  const age = character.age || 25;
  const gender = character.gender || "female";
  segments.push(`${name}, ${age}-year-old ${gender}`);

  const physiqueFeatures: string[] = [];
  if (character.figure) {
    physiqueFeatures.push(`${character.figure} build`);
  }
  if (character.ethnicity) {
    const normalizedEthnicity = character.ethnicity.trim().toLowerCase();
    const ethnicityMap: Record<string, string> = {
      middle_eastern: "Middle Eastern with olive skin",
      "middle eastern": "Middle Eastern with olive skin",
      latina: "Latina with tan skin",
      black: "Black with dark skin, African heritage",
    };
    physiqueFeatures.push(ethnicityMap[normalizedEthnicity] ?? character.ethnicity);
  }
  if (character.hairColor) {
    const normalizedHair = character.hairColor.trim().toLowerCase();
    const hairLabel = normalizedHair === "unnatural_colors" ? "pink" : character.hairColor;
    physiqueFeatures.push(`${hairLabel} hair`);
  }
  if (character.breastSize) {
    const normalizedBreast = character.breastSize.trim().toLowerCase();
    const breastLabel =
      normalizedBreast === "small"
        ? "small breasts"
        : normalizedBreast === "medium"
          ? "medium breasts"
          : normalizedBreast === "large"
            ? "large breasts"
            : `${character.breastSize} breasts`;
    physiqueFeatures.push(breastLabel);
  }
  if (physiqueFeatures.length > 0) {
    segments.push(physiqueFeatures.join(", "));
  }

  return segments.join(", ");
}

function buildBaseImagePrompt(character: Character): string {
  const segments: string[] = [];
  segments.push(buildIdentityPromptPrefix(character));
  segments.push("casual elegant attire");
  segments.push("cinematic photo, high dynamic range, sharp focus, ultra detailed");
  return segments.join(", ");
}

/**
 * Builds an image generation prompt following the structure:
 * [base prompt], [action / pose], [environment + lighting],
 * [camera / style], [quality tags]
 */
export function buildProfilePhotoPrompt(character: Character): string {
  const segments: string[] = [];
  const basePrompt =
    normalizePrompt(character.imageBasePrompt) ?? buildBaseImagePrompt(character);
  segments.push(basePrompt);

  segments.push("relaxed pose, slight smile, looking at camera");
  segments.push("soft natural lighting, neutral background, soft shadows");
  segments.push("85mm f/1.4, three-quarter head-and-shoulders portrait");

  if (!basePrompt.toLowerCase().includes("cinematic photo")) {
    segments.push("cinematic photo, high dynamic range, sharp focus");
  }

  return segments.join(", ");
}

